import { extractInputOutputMessages, extractToolAttributes } from './openinference';

const LIVEKIT_SPAN_NAMES = new Set([
  'agent_session', 'user_turn', 'assistant_turn', 'llm_node', 'llm_request',
  'tts_request', 'tts_node', 'tts_request_run', 'stt_request', 'stt_node',
  'user_speaking', 'agent_speaking', 'eou_detection', 'on_enter', 'on_exit',
  'start_agent_activity', 'drain_agent_activity'
]);

export const isLiveKitSpan = (span) => {
  const name = span?.name || span?.span_name || '';
  return LIVEKIT_SPAN_NAMES.has(name);
};

const parseAttributes = (attributes) => {
  if (!attributes) return null;
  if (typeof attributes === 'string') {
    try {
      return JSON.parse(attributes);
    } catch {
      return null;
    }
  }
  return attributes;
};

const parseJsonMaybe = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const extractMessageTextFromValue = (value) => {
  const parsed = parseJsonMaybe(value);
  if (!parsed || typeof parsed !== 'object') return null;

  // OpenAI-like: { messages: [...] } or { choices: [{ message: { content } }] }
  if (Array.isArray(parsed.messages)) {
    return parsed.messages
      .map(msg => msg?.content)
      .filter(Boolean)
      .join('\n');
  }

  if (Array.isArray(parsed.choices) && parsed.choices[0]?.message?.content) {
    return parsed.choices
      .map(choice => choice?.message?.content)
      .filter(Boolean)
      .join('\n');
  }

  return null;
};

export const extractLiveKitInput = (span) => {
  try {
    const attrs = parseAttributes(span.attributes) || {};
    const sections = [];
    const lk = attrs.lk || {};

    if (lk.user_input) {
      sections.push({ title: 'User Input', content: lk.user_input, type: 'text' });
    }
    if (lk.input_text) {
      sections.push({ title: 'Text to Synthesize', content: lk.input_text, type: 'text' });
    }
    if (lk.chat_ctx) {
      let chatCtx = lk.chat_ctx;
      if (typeof chatCtx === 'string') {
        try { chatCtx = JSON.parse(chatCtx); } catch { /* keep as string */ }
      }
      sections.push({ title: 'Conversation Context', content: chatCtx, type: 'json' });
    }
    if (lk.function_tools) {
      let tools = lk.function_tools;
      if (typeof tools === 'string') {
        try { tools = JSON.parse(tools); } catch { /* keep as string */ }
      }
      sections.push({ title: 'Available Tools', content: tools, type: 'json' });
    }

    return sections;
  } catch {
    return [];
  }
};

export const extractLiveKitOutput = (span) => {
  try {
    const attrs = parseAttributes(span.attributes) || {};
    const sections = [];
    const lk = attrs.lk || {};

    if (lk.user_transcript) {
      sections.push({ title: 'User Transcript', content: lk.user_transcript, type: 'transcript', icon: 'mic' });
    }

    if (lk.response?.text) {
      sections.push({ title: 'Agent Response', content: lk.response.text, type: 'response', icon: 'message' });
    }

    if (lk.tts_metrics) {
      let metrics = lk.tts_metrics;
      if (typeof metrics === 'string') {
        try { metrics = JSON.parse(metrics); } catch { /* keep as string */ }
      }
      sections.push({ title: 'TTS Metrics', content: metrics, type: 'metrics', icon: 'volume' });
    }

    if (lk.llm_metrics) {
      let metrics = lk.llm_metrics;
      if (typeof metrics === 'string') {
        try { metrics = JSON.parse(metrics); } catch { /* keep as string */ }
      }
      sections.push({ title: 'LLM Metrics', content: metrics, type: 'metrics', icon: 'clock' });
    }

    if (lk.response?.function_calls) {
      let functionCalls = lk.response.function_calls;
      if (typeof functionCalls === 'string') {
        try { functionCalls = JSON.parse(functionCalls); } catch { /* keep as string */ }
      }
      if (functionCalls && (Array.isArray(functionCalls) ? functionCalls.length > 0 : true)) {
        sections.push({ title: 'Function Calls', content: functionCalls, type: 'json', icon: 'message' });
      }
    }

    if (lk.eou?.probability !== undefined) {
      const eouData = {
        probability: lk.eou.probability,
        language: lk.eou.language,
        endpointing_delay: lk.eou.endpointing_delay,
        unlikely_threshold: lk.eou.unlikely_threshold
      };
      sections.push({ title: 'End of Utterance Detection', content: eouData, type: 'metrics', icon: 'mic' });
    }

    return sections;
  } catch {
    return [];
  }
};

export const extractRetrieverDocuments = (span) => {
  try {
    const attrs = parseAttributes(span.attributes) || {};
    let rawDocuments = [];

    const flatDocs = attrs?.['retrieval.documents'];
    if (flatDocs) {
      try {
        rawDocuments = typeof flatDocs === 'string' ? JSON.parse(flatDocs) : flatDocs;
      } catch {
        // ignore parse errors
      }
    }

    if (rawDocuments.length === 0) {
      const retrieverAttributes = attrs?.retrieval || {};
      const nestedDocs = retrieverAttributes.documents || [];
      if (Array.isArray(nestedDocs)) {
        rawDocuments = nestedDocs.map(obj => obj.document).filter(Boolean);
      }
    }

    return (Array.isArray(rawDocuments) ? rawDocuments : []).map(doc => {
      const id = doc['document.id'] || doc.id;
      const content = doc['document.content'] || doc.content;
      const score = doc['document.score'] ?? doc.score;
      let metadata = doc['document.metadata'] || doc.metadata;
      if (typeof metadata === 'string') {
        try { metadata = JSON.parse(metadata); } catch { /* ignore */ }
      }
      return { id, content, score, metadata };
    });
  } catch {
    return [];
  }
};

const joinMessages = (messages) => messages
  .map(msg => msg.content)
  .filter(Boolean)
  .join('\n');

export const getSpanInputSections = (span, spanKind) => {
  const sections = [];
  const attrs = parseAttributes(span?.attributes) || {};

  if (span?.input_value) {
    const messageText = extractMessageTextFromValue(span.input_value);
    if (messageText) {
      sections.push({ title: 'Input', content: messageText, type: 'text' });
    } else {
      sections.push({ title: 'Input', content: parseJsonMaybe(span.input_value), type: 'json' });
    }
  } else if (attrs?.input?.value) {
    const messageText = extractMessageTextFromValue(attrs.input.value);
    if (messageText) {
      sections.push({ title: 'Input', content: messageText, type: 'text' });
    } else {
      sections.push({ title: 'Input', content: parseJsonMaybe(attrs.input.value), type: 'json' });
    }
  }

  if (spanKind === 'llm' && !span?.input_value) {
    const { input } = extractInputOutputMessages(span.attributes);
    if (input && input.length > 0) {
      sections.push({ title: 'Input Messages', content: joinMessages(input), type: 'text' });
    }
  }

  if (spanKind === 'tool' && !span?.input_value) {
    const { input } = extractToolAttributes(span.attributes);
    if (input) {
      sections.push({ title: 'Tool Input', content: input, type: 'json' });
    }
  }

  if (isLiveKitSpan(span)) {
    extractLiveKitInput(span).forEach(section => sections.push(section));
  }

  return sections;
};

export const getSpanOutputSections = (span, spanKind) => {
  const sections = [];
  const attrs = parseAttributes(span?.attributes) || {};

  if (span?.output_value) {
    const messageText = extractMessageTextFromValue(span.output_value);
    if (messageText) {
      sections.push({ title: 'Output', content: messageText, type: 'text' });
    } else {
      sections.push({ title: 'Output', content: parseJsonMaybe(span.output_value), type: 'json' });
    }
  } else if (attrs?.output?.value) {
    const messageText = extractMessageTextFromValue(attrs.output.value);
    if (messageText) {
      sections.push({ title: 'Output', content: messageText, type: 'text' });
    } else {
      sections.push({ title: 'Output', content: parseJsonMaybe(attrs.output.value), type: 'json' });
    }
  }

  if (spanKind === 'llm' && !span?.output_value) {
    const { output } = extractInputOutputMessages(span.attributes);
    if (output && output.length > 0) {
      sections.push({ title: 'Output Messages', content: joinMessages(output), type: 'text' });
    }
  }

  if (spanKind === 'tool' && !span?.output_value) {
    const { output } = extractToolAttributes(span.attributes);
    if (output) {
      sections.push({ title: 'Tool Output', content: output, type: 'json' });
    }
  }

  if (spanKind === 'retriever') {
    const documents = extractRetrieverDocuments(span);
    if (documents.length > 0) {
      sections.push({ title: `Documents (${documents.length})`, content: documents, type: 'json' });
    }
  }

  if (isLiveKitSpan(span)) {
    extractLiveKitOutput(span).forEach(section => sections.push(section));
  }

  return sections;
};
